import { useQuery } from "@tanstack/react-query";
import AppConstants from "../../constants/AppConstants";
import { USER_LIBRARY_DESIGNS } from "../../constants/QueryKeys";
import UserService from "../../service/UserService";
import { getUserId } from "./useAccount";
import { STATIC_QUERY_DEFAULTS } from "../../utils/QueryUtils";

/** @typedef {import("@/types/services/LibraryServiceTypes.mjs").SiteConfig} SiteConfig */
/** @typedef {{ designs: SiteConfig[] }} UserLibraryDesignsData */
/**
 * @template T
 * @typedef {import("@/types/TypeUtils.mjs").QueryResultRest<T>} QueryResultRest
 */
/**
 * @template T
 * @typedef {import("@/types/TypeUtils.mjs").QueryHookOptions<T>} QueryHookOptions
 */

const defaults = { ...STATIC_QUERY_DEFAULTS, enabled: false };

/**
 * Key + fetcher for a user's library designs; imperative callers spread this into fetchQuery.
 * @param {number} userId
 * @param {string} designType
 */
export const userLibraryDesignsQuery = (userId, designType) => ({
  queryKey: [USER_LIBRARY_DESIGNS, userId, designType],
  queryFn: () =>
    /** @type {Promise<UserLibraryDesignsData>} */ (
      UserService.loadLibraryDesigns(userId, designType)
    ),
  staleTime: Infinity,
});

/**
 * The current user's saved library designs (disabled until a caller enables or refetches it).
 * @param {QueryHookOptions<UserLibraryDesignsData>} [options] - Extra `useQuery` options, spread over the defaults.
 * @returns {{ userLibDesigns: SiteConfig[] | undefined } & QueryResultRest<UserLibraryDesignsData>}
 */
const useUserLibraryDesigns = (options = {}) => {
  const userId = getUserId();
  const designType = AppConstants.DESIGN_TYPE_SITE;

  const { data, ...rest } = useQuery({
    ...userLibraryDesignsQuery(userId, designType),
    ...defaults,
    ...options,
  });
  return { userLibDesigns: data?.designs, ...rest };
};

export default useUserLibraryDesigns;
